/**
 * React Context for the active NWC connection override.
 *
 * Evolu's db.update for isActive doesn't reliably trigger reactive
 * query updates. This context provides immediate React state for
 * which connection is active, shared across all components.
 *
 * Tier: 2 (Lightning features).
 *
 * @module contexts/ActiveConnectionProvider
 */

import { createContext, useContext, useState, useCallback } from 'react';

/** Shape of the active connection context. */
interface ActiveConnectionContextValue {
  /** Overridden active connection ID, or null (use Evolu default). */
  readonly activeOverrideId: string | null;
  /** Set the active connection ID override. */
  readonly setActiveOverrideId: (id: string) => void;
}

const ActiveConnectionContext = createContext<ActiveConnectionContextValue | null>(null);

/** Props for ActiveConnectionProvider. */
interface ActiveConnectionProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Provide shared active connection state to the component tree.
 *
 * @param props - Provider props with children
 * @returns Provider element
 */
export function ActiveConnectionProvider({
  children,
}: ActiveConnectionProviderProps): React.ReactElement {
  const [activeOverrideId, setOverride] = useState<string | null>(null);

  const setActiveOverrideId = useCallback((id: string): void => {
    setOverride(id);
  }, []);

  return (
    <ActiveConnectionContext.Provider value={{ activeOverrideId, setActiveOverrideId }}>
      {children}
    </ActiveConnectionContext.Provider>
  );
}

/**
 * Access the shared active connection override.
 *
 * Returns null if called outside ActiveConnectionProvider (graceful fallback).
 *
 * @returns Context value or null
 */
export function useActiveConnectionOverride(): ActiveConnectionContextValue | null {
  return useContext(ActiveConnectionContext);
}
